import React, { useState, useEffect } from 'react';
import { Edit2, X, Users, Plus, Trash2, Upload, Pencil, RefreshCw, Eye, ChevronRight, ChevronDown, UserPlus, Users as SubordinatesIcon } from 'lucide-react';
import Swal from 'sweetalert2';
import axios from 'axios';
import JoditEditor from 'jodit-react';

const API_BASE_URL = 'https://backend.dlapakistan.com/api/board';
const emerald600 = "#059669";

const MeettheBoard = () => {
  const [headerData, setHeaderData] = useState({
    main_heading: "Meet the Board",
    description: "The directors of the DLA board are elected to their positions by the DLA members at the AGM. The President and Vice President of the DLA board are elected on a five-year term, the rest of the DLA board members are elected on a three-year term."
  });

  const [boardMembers, setBoardMembers] = useState([]);
  const [expandedMembers, setExpandedMembers] = useState({});
  const [showHeadModal, setShowHeadModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [tempHead, setTempHead] = useState({});
  const [memberForm, setMemberForm] = useState({
    name: '',
    position: '',
    description: '',
    image: null,
    parent_id: null
  });
  const [editingMember, setEditingMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({
    name: false,
    position: false
  });

  const joditConfig = {
    height: 300,
    toolbarButtonSize: 'medium',
    toolbarAdaptive: false,
    showCharsCounter: true,
    showWordsCounter: true,
    showXPathInStatusbar: false,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_only_text',
    buttons: [
      'source', '|',
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'ul', 'ol', 'outdent', 'indent', '|',
      'font', 'fontsize', 'brush', 'paragraph', '|',
      'image', 'video', 'table', 'link', '|',
      'align', 'undo', 'redo', '|',
      'hr', 'eraser', 'fullsize'
    ],
    editorCssClass: 'custom-jodit-editor',
    style: {
      fontSize: '14px',
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getApiClient = () => {
    return axios.create({ baseURL: 'https://backend.dlapakistan.com' });
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `https://backend.dlapakistan.com${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const headerRes = await axios.get(`${API_BASE_URL}/header/active/`);
      if (headerRes.data) {
        setHeaderData(headerRes.data);
        setTempHead(headerRes.data);
      }

      const membersRes = await axios.get(`${API_BASE_URL}/members/active/`);
      const processed = membersRes.data.map(m => ({
        ...m,
        image_url: m.image_url ? getImageUrl(m.image_url) : null,
        subordinates: m.subordinates?.map(sub => ({
          ...sub,
          image_url: sub.image_url ? getImageUrl(sub.image_url) : null
        })) || []
      }));
      setBoardMembers(processed);
    } catch (err) {
      Swal.fire({
        title: 'Error',
        text: 'Failed to fetch data',
        icon: 'error',
        confirmButtonColor: emerald600
      });
    } finally {
      setLoading(false);
    }
  };

  const saveHeader = async () => {
    setSaving(true);
    try {
      const api = getApiClient();
      const headers = await api.get(`${API_BASE_URL}/header/`);
      let res;
      if (headers.data?.length > 0) {
        res = await api.patch(`${API_BASE_URL}/header/${headers.data[0].id}/`, {
          ...tempHead,
          is_active: true
        });
      } else {
        res = await api.post(`${API_BASE_URL}/header/`, {
          ...tempHead,
          is_active: true
        });
      }
      setHeaderData(res.data);
      setShowHeadModal(false);
      Swal.fire({
        title: 'Saved!',
        icon: 'success',
        timer: 1400,
        showConfirmButton: false,
        confirmButtonColor: emerald600
      });
    } catch (err) {
      Swal.fire({
        title: 'Error',
        text: 'Failed to save header',
        icon: 'error',
        confirmButtonColor: emerald600
      });
    } finally {
      setSaving(false);
    }
  };

  const saveMember = async () => {
    let hasErrors = false;
    const newErrors = { name: false, position: false };
    if (!memberForm.name.trim()) { newErrors.name = true; hasErrors = true; }
    if (!memberForm.position.trim()) { newErrors.position = true; hasErrors = true; }
    setFormErrors(newErrors);

    if (hasErrors) {
      Swal.fire({
        title: 'Required Fields Missing',
        html: `
          <div class="text-left">
            <p class="mb-2">Please fill in:</p>
            <ul class="list-disc pl-5">
              ${newErrors.name ? '<li><strong>Full Name</strong></li>' : ''}
              ${newErrors.position ? '<li><strong>Position</strong></li>' : ''}
            </ul>
          </div>`,
        icon: 'warning',
        confirmButtonColor: emerald600
      });
      return;
    }

    setSaving(true);
    try {
      const api = getApiClient();
      let res;

      const submissionData = {
        name: memberForm.name.trim(),
        position: memberForm.position.trim(),
        description: memberForm.description || '',
        is_active: true,
        parent: memberForm.parent_id || null
      };

      if (editingMember) {
        if (memberForm.image || !editingMember.image_url) {
          const formData = new FormData();
          Object.keys(submissionData).forEach(key => {
            if (submissionData[key] !== null && submissionData[key] !== undefined) {
              formData.append(key, submissionData[key]);
            }
          });
          if (memberForm.image) formData.append('image', memberForm.image);
          res = await api.put(`${API_BASE_URL}/members/${editingMember.id}/`, formData);
        } else {
          res = await api.patch(`${API_BASE_URL}/members/${editingMember.id}/`, submissionData);
        }
      } else {
        const formData = new FormData();
        Object.keys(submissionData).forEach(key => {
          if (submissionData[key] !== null && submissionData[key] !== undefined) {
            formData.append(key, submissionData[key]);
          }
        });
        if (memberForm.image) formData.append('image', memberForm.image);
        res = await api.post(`${API_BASE_URL}/members/`, formData);
      }

      closeMemberModal();
      fetchData();
      Swal.fire({
        title: editingMember ? 'Updated!' : 'Added!',
        icon: 'success',
        timer: 1800,
        showConfirmButton: false,
        confirmButtonColor: emerald600
      });
    } catch (err) {
      Swal.fire({
        title: 'Error',
        text: 'Failed to save member',
        icon: 'error',
        confirmButtonColor: emerald600
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteMember = async (id, isSubordinate = false) => {
    const result = await Swal.fire({
      title: 'Delete member?',
      text: "This can't be undone",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete'
    });

    if (!result.isConfirmed) return;

    try {
      const api = getApiClient();
      await api.delete(`${API_BASE_URL}/members/${id}/`);
      fetchData();
      Swal.fire({
        title: 'Deleted!',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
        confirmButtonColor: emerald600
      });
    } catch (err) {
      Swal.fire({
        title: 'Delete failed',
        icon: 'error',
        confirmButtonColor: emerald600
      });
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        title: 'File too large',
        text: 'Max 5MB allowed',
        icon: 'warning',
        confirmButtonColor: emerald600
      });
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      Swal.fire({
        title: 'Invalid file',
        text: 'Only JPG, PNG, GIF, WebP allowed',
        icon: 'warning',
        confirmButtonColor: emerald600
      });
      return;
    }

    setMemberForm(prev => ({ ...prev, image: file }));
  };

  const handleEditMember = (member, isSubordinate = false, parentMember = null) => {
    setEditingMember(member);
    setMemberForm({
      name: member.name,
      position: member.position,
      description: member.description || '',
      image: null,
      parent_id: member.parent || (parentMember ? parentMember.id : null)
    });
    setFormErrors({ name: false, position: false });
    setShowMemberModal(true);
    if (!isSubordinate) {
      setShowDetailModal(false);
    }
  };

  const handleAddSubordinate = (parentMember) => {
    setEditingMember(null);
    setMemberForm({
      name: '',
      position: '',
      description: '',
      image: null,
      parent_id: parentMember.id
    });
    setFormErrors({ name: false, position: false });
    setShowMemberModal(true);
  };

  const handleViewImage = (url) => {
    if (url) {
      setSelectedImage(getImageUrl(url));
      setShowImageModal(true);
    }
  };

  const handleReadMore = (member) => {
    setSelectedMember(member);
    setShowDetailModal(true);
  };

  const toggleExpand = (memberId) => {
    setExpandedMembers(prev => ({
      ...prev,
      [memberId]: !prev[memberId]
    }));
  };

  const closeHeadModal = () => {
    setTempHead(headerData);
    setShowHeadModal(false);
  };

  const closeMemberModal = () => {
    setMemberForm({ name: '', position: '', description: '', image: null, parent_id: null });
    setEditingMember(null);
    setFormErrors({ name: false, position: false });
    setShowMemberModal(false);
  };

  const closeDetailModal = () => {
    setSelectedMember(null);
    setShowDetailModal(false);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setShowImageModal(false);
  };

  const refreshData = () => {
    fetchData();
    Swal.fire({
      title: 'Refreshed',
      icon: 'success',
      timer: 1200,
      showConfirmButton: false,
      confirmButtonColor: emerald600
    });
  };

  const handleInputChange = (field, value) => {
    setMemberForm(prev => ({ ...prev, [field]: value }));
    if (value.trim() && formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  if (loading) {
    return (
      <div className="flex-1 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading board data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full">

      {/* HEADER */}
      <div className="px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mt-12 md:mt-0">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-900">Board Management</h1>
            <p className="text-gray-600 mt-1">Manage board members, team members & header content</p>
          </div>

          <div className="flex flex-wrap gap-4">
            <button
              onClick={refreshData}
              className="p-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all shadow-sm"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5 text-gray-600" />
            </button>

            <button
              onClick={() => setShowHeadModal(true)}
              className="flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all"
              style={{ backgroundColor: emerald600 }}
            >
              <Edit2 className="w-5 h-5 mr-2" />
              Edit Header
            </button>
          </div>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="px-8 md:px-12 w-full pb-20">
        <div className="space-y-16">
          {/* Header Content Section */}
          <div className="animate-fadeIn text-left w-full bg-transparent">
            <div className="mb-10 border-l-4 pl-6" style={{ borderColor: emerald600 }}>
              <h2 className="text-4xl font-bold text-gray-900 tracking-tight">
                {headerData.main_heading || "Meet the Board"}
              </h2>
            </div>

            <div className="max-w-4xl mb-16">
              <div className="prose prose-lg text-gray-700 leading-relaxed">
                {headerData.description ? (
                  <div dangerouslySetInnerHTML={{ __html: headerData.description }} />
                ) : (
                  <p>
                    The directors of the DLA board are elected to their positions by the DLA members at the AGM.
                    The President and Vice President of the DLA board are elected on a five-year term, the rest of
                    the DLA board members are elected on a three-year term.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Board Members Section */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-10">
              <div className="flex items-center gap-3">
                <Users className="text-emerald-600" size={24} />
                <h3 className="text-2xl font-bold text-gray-900">Board Members & Teams</h3>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm font-medium">
                  {boardMembers.length} {boardMembers.length === 1 ? 'Member' : 'Members'}
                </span>
              </div>
              <button
                onClick={() => {
                  setEditingMember(null);
                  setMemberForm({ name: '', position: '', description: '', image: null, parent_id: null });
                  setShowMemberModal(true);
                }}
                className="flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all"
                style={{ backgroundColor: emerald600 }}
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Board Member
              </button>
            </div>

            {boardMembers.length === 0 ? (
              <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl">
                <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-2xl font-semibold text-gray-700 mb-4">No board members added yet</h3>
                <p className="text-gray-500 mb-10 max-w-lg mx-auto">
                  Start by adding your first board member to display here
                </p>
                <button
                  onClick={() => {
                    setEditingMember(null);
                    setMemberForm({ name: '', position: '', description: '', image: null, parent_id: null });
                    setShowMemberModal(true);
                  }}
                  className="inline-flex items-center px-8 py-4 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all text-lg text-white"
                  style={{ backgroundColor: emerald600 }}
                >
                  <Plus className="w-6 h-6 mr-3" />
                  Add First Member
                </button>
              </div>
            ) : (
              <div className="space-y-8">
                {boardMembers.map(member => {
                  const img = member.image_url;
                  const plainDescription = stripHtml(member.description || '');
                  const isExpanded = expandedMembers[member.id];
                  const hasSubordinates = member.subordinates && member.subordinates.length > 0;

                  return (
                    <div key={member.id} className="border rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all">
                      {/* Board Member Card */}
                      <div className="p-6">
                        <div className="flex flex-col md:flex-row gap-6">
                          {/* Image */}
                          <div className="relative w-full md:w-48 h-48 flex-shrink-0">
                            <div className="w-full h-full rounded-xl overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                              {img ? (
                                <img
                                  src={img}
                                  alt={member.name}
                                  className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                                  onClick={() => handleViewImage(member.image_url)}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-emerald-50 text-emerald-700 text-4xl font-bold">
                                  {member.name?.[0]?.toUpperCase() || '?'}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Info */}
                          <div className="flex-1">
                            <div className="flex flex-wrap items-start justify-between gap-4">
                              <div>
                                <h4 className="text-2xl font-bold text-gray-900 mb-2">{member.name}</h4>
                                <p className="text-emerald-600 font-medium text-lg mb-3">{member.position}</p>
                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                  {hasSubordinates && (
                                    <span className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                                      <SubordinatesIcon size={14} />
                                      {member.subordinates.length} Team {member.subordinates.length === 1 ? 'Member' : 'Members'}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditMember(member)}
                                  className="p-2.5 bg-gray-100 rounded-lg hover:bg-blue-100 transition-all duration-200 group"
                                  title="Edit Member"
                                >
                                  <Edit2 className="w-5 h-5 text-gray-600 group-hover:text-blue-600 transition-colors" />
                                </button>
                                <button
                                  onClick={() => deleteMember(member.id)}
                                  className="p-2.5 bg-gray-100 rounded-lg hover:bg-red-100 transition-all duration-200 group"
                                  title="Delete Member"
                                >
                                  <Trash2 className="w-5 h-5 text-gray-600 group-hover:text-red-600 transition-colors" />
                                </button>
                                <button
                                  onClick={() => handleAddSubordinate(member)}
                                  className="p-2.5 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-all duration-200 group"
                                  title="Add Team Member"
                                >
                                  <UserPlus className="w-5 h-5 text-emerald-600 group-hover:text-emerald-700 transition-colors" />
                                </button>
                              </div>
                            </div>

                            <div className="mt-4">
                              <div className="text-gray-600 line-clamp-2 mb-4">
                                {plainDescription
                                  ? plainDescription.length > 150
                                    ? `${plainDescription.substring(0, 150)}...`
                                    : plainDescription
                                  : "No biography available."}
                              </div>
                              <div className="flex gap-3">
                                <button
                                  onClick={() => handleReadMore(member)}
                                  className="px-4 py-2 text-emerald-600 font-medium hover:bg-emerald-50 rounded-lg transition-all flex items-center gap-2"
                                >
                                  Read Biography
                                  <ChevronRight size={16} />
                                </button>
                                {hasSubordinates && (
                                  <button
                                    onClick={() => toggleExpand(member.id)}
                                    className="px-4 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2"
                                  >
                                    {isExpanded ? 'Hide Team' : 'Show Team'}
                                    <ChevronDown size={16} className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Subordinates Section */}
                      {isExpanded && hasSubordinates && (
                        <div className="border-t bg-gray-50 p-6">
                          <div className="flex items-center justify-between mb-4">
                            <h5 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                              <SubordinatesIcon size={20} className="text-emerald-600" />
                              Team Members ({member.subordinates.length})
                            </h5>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {member.subordinates.map(sub => (
                              <div key={sub.id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all">
                                <div className="flex gap-4">
                                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                                    {sub.image_url ? (
                                      <img
                                        src={sub.image_url}
                                        alt={sub.name}
                                        className="w-full h-full object-cover cursor-pointer"
                                        onClick={() => handleViewImage(sub.image_url)}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center bg-emerald-50 text-emerald-700 font-bold text-xl">
                                        {sub.name?.[0]?.toUpperCase()}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h6 className="font-semibold text-gray-900 truncate">{sub.name}</h6>
                                    <p className="text-sm text-emerald-600 truncate">{sub.position}</p>
                                    <div className="flex gap-2 mt-2">
                                      <button
                                        onClick={() => handleEditMember(sub, true, member)}
                                        className="p-1.5 bg-blue-50 rounded-md hover:bg-blue-100 transition-all duration-200 group"
                                        title="Edit Team Member"
                                      >
                                        <Edit2 size={14} className="text-blue-600 group-hover:text-blue-700" />
                                      </button>
                                      <button
                                        onClick={() => deleteMember(sub.id, true)}
                                        className="p-1.5 bg-red-50 rounded-md hover:bg-red-100 transition-all duration-200 group"
                                        title="Delete Team Member"
                                      >
                                        <Trash2 size={14} className="text-red-600 group-hover:text-red-700" />
                                      </button>
                                      <button
                                        onClick={() => handleReadMore(sub)}
                                        className="p-1.5 bg-emerald-50 rounded-md hover:bg-emerald-100 transition-all duration-200 group"
                                        title="View Biography"
                                      >
                                        <Eye size={14} className="text-emerald-600 group-hover:text-emerald-700" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Header Edit Modal */}
      {showHeadModal && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeHeadModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl pointer-events-auto overflow-hidden animate-modalIn border border-gray-100">
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-800">Edit Board Header</h2>
                <button onClick={closeHeadModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                  <X size={20} />
                </button>
              </div>

              <form className="p-8 space-y-6 overflow-y-auto max-h-[80vh]" onSubmit={(e) => { e.preventDefault(); saveHeader(); }}>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Main Heading</label>
                  <input
                    value={tempHead.main_heading || ''}
                    onChange={e => setTempHead({ ...tempHead, main_heading: e.target.value })}
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': emerald600 }}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Description</label>
                  <textarea
                    value={tempHead.description || ''}
                    onChange={e => setTempHead({ ...tempHead, description: e.target.value })}
                    rows={5}
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 transition-all resize-none"
                    style={{ '--tw-ring-color': emerald600 }}
                    required
                  />
                </div>

                <button
                  type="submit"
                  style={{ backgroundColor: emerald600 }}
                  className={`w-full py-4 text-white rounded-2xl font-bold shadow-xl hover:brightness-110 transition-all text-lg flex items-center justify-center ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : 'Save Header'}
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Member Add/Edit Modal */}
      {showMemberModal && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeMemberModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl pointer-events-auto overflow-hidden animate-modalIn border border-gray-100">
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingMember ? 'Edit Member' : (memberForm.parent_id ? 'Add Team Member' : 'Add Board Member')}
                </h2>
                <button onClick={closeMemberModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                  <X size={20} />
                </button>
              </div>

              <div className="p-8 space-y-6 overflow-y-auto max-h-[80vh]">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {/* Image Upload */}
                  <div className="md:col-span-1">
                    <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center hover:bg-gray-50 transition-colors h-full">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="member-image"
                      />
                      <label htmlFor="member-image" className="cursor-pointer block h-full">
                        {memberForm.image ? (
                          <div>
                            <img
                              src={URL.createObjectURL(memberForm.image)}
                              alt="preview"
                              className="w-full h-48 object-cover rounded-xl mx-auto mb-4 shadow-md"
                            />
                            <span className="text-sm text-emerald-600 mt-3 inline-block font-medium">
                              Change photo
                            </span>
                          </div>
                        ) : editingMember?.image_url ? (
                          <div>
                            <img
                              src={getImageUrl(editingMember.image_url)}
                              alt="current"
                              className="w-full h-48 object-cover rounded-xl mx-auto mb-4 shadow-md"
                            />
                            <span className="text-sm text-emerald-600 mt-3 inline-block font-medium">
                              Change photo
                            </span>
                          </div>
                        ) : (
                          <div className="py-8 h-full flex flex-col justify-center">
                            <Upload className="mx-auto text-gray-400 mb-3" size={48} />
                            <p className="text-gray-600 text-lg">Upload photo</p>
                            <p className="text-sm text-gray-400 mt-2">JPG / PNG • max 5MB</p>
                            <span className="text-sm text-emerald-600 mt-4 inline-block font-medium">
                              Select photo (optional)
                            </span>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="md:col-span-2 space-y-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        value={memberForm.name}
                        onChange={e => handleInputChange('name', e.target.value)}
                        className={`w-full px-5 py-3 border rounded-2xl outline-none focus:ring-2 transition-all ${
                          formErrors.name ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50'
                        }`}
                        style={{ '--tw-ring-color': formErrors.name ? '#ef4444' : emerald600 }}
                        required
                      />
                      {formErrors.name && (
                        <p className="mt-1 text-sm text-red-600">Required field</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                        Position / Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        value={memberForm.position}
                        onChange={e => handleInputChange('position', e.target.value)}
                        className={`w-full px-5 py-3 border rounded-2xl outline-none focus:ring-2 transition-all ${
                          formErrors.position ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50'
                        }`}
                        style={{ '--tw-ring-color': formErrors.position ? '#ef4444' : emerald600 }}
                        required
                      />
                      {formErrors.position && (
                        <p className="mt-1 text-sm text-red-600">Required field</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Biography Editor */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">
                    Biography / About
                  </label>
                  <div className="border border-gray-200 rounded-2xl overflow-hidden">
                    <JoditEditor
                      value={memberForm.description}
                      config={joditConfig}
                      onBlur={newContent => setMemberForm(prev => ({ ...prev, description: newContent }))}
                      onChange={() => {}}
                    />
                  </div>
                </div>

                {/* Note for subordinates */}
                {memberForm.parent_id && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-sm text-blue-800">
                      This team member will be listed under the board member they report to.
                    </p>
                  </div>
                )}

                {/* Save Button */}
                <button
                  onClick={saveMember}
                  disabled={saving}
                  style={{ backgroundColor: emerald600 }}
                  className={`w-full py-4 text-white rounded-2xl font-bold shadow-xl hover:brightness-110 transition-all text-lg flex items-center justify-center ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : editingMember ? 'Update Member' : (memberForm.parent_id ? 'Add Team Member' : 'Add Board Member')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Detail / Biography Modal */}
      {showDetailModal && selectedMember && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeDetailModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto pointer-events-auto animate-modalIn border border-gray-100">
              <div className="sticky top-0 bg-white/90 backdrop-blur-md px-8 py-5 border-b flex justify-between items-center z-10">
                <h2 className="text-xl font-bold text-gray-800">Member Profile</h2>
                <button onClick={closeDetailModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                  <X size={20} />
                </button>
              </div>

              <div className="p-8">
                <div className="flex flex-col md:flex-row gap-8">
                  {/* Image Column */}
                  <div className="w-full md:w-1/3">
                    <img
                      src={selectedMember.image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedMember.name)}&background=059669&color=fff&size=400`}
                      alt={selectedMember.name}
                      className="w-full h-auto rounded-2xl shadow-lg object-cover mb-6"
                    />
                    <button
                      onClick={() => {
                        closeDetailModal();
                        handleEditMember(selectedMember);
                      }}
                      className="w-full py-3 rounded-xl font-medium shadow-md hover:opacity-90 transition-all text-white flex items-center justify-center gap-2"
                      style={{ backgroundColor: emerald600 }}
                    >
                      <Edit2 className="w-5 h-5" />
                      Edit Profile
                    </button>
                  </div>

                  {/* Bio Column */}
                  <div className="w-full md:w-2/3">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{selectedMember.name}</h1>
                    <p className="text-xl text-emerald-600 font-medium mb-8">{selectedMember.position}</p>

                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-4">Biography</h3>
                      <div className="bg-gray-50 rounded-2xl p-6 prose prose-lg max-w-none">
                        {selectedMember.description ? (
                          <div dangerouslySetInnerHTML={{ __html: selectedMember.description }} />
                        ) : (
                          <div className="text-center py-12 text-gray-500">
                            No biography added yet.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Full Image View Modal */}
      {showImageModal && selectedImage && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/95 animate-fadeIn" onClick={closeImageModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="relative max-w-6xl max-h-[90vh] pointer-events-auto">
              <button
                className="absolute -top-16 right-0 text-white hover:text-gray-300 transition-colors"
                onClick={closeImageModal}
              >
                <X size={48} />
              </button>
              <img
                src={selectedImage}
                alt="Full size member photo"
                className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </>
      )}

      {/* STYLES */}
      <style jsx>{`
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(10px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        @keyframes modalIn { 
          from { opacity: 0; transform: scale(0.95); } 
          to { opacity: 1; transform: scale(1); } 
        }
        .animate-fadeIn { 
          animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        .animate-modalIn { 
          animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        @media (max-width: 640px) {
          .px-8 {
            padding-left: 1rem;
            padding-right: 1rem;
          }
          
          .text-3xl {
            font-size: 1.5rem;
          }
          
          .text-4xl {
            font-size: 1.875rem;
          }
        }
        
        @media (max-width: 768px) {
          .p-8 {
            padding: 1.5rem;
          }
        }
        
        @media (min-width: 1536px) {
          .px-12 {
            padding-left: 3rem;
            padding-right: 3rem;
          }
        }
      `}</style>
    </div>
  );
};

export default MeettheBoard;